import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from '@/lib/api-client';
import {
  applyResolvedTheme,
  clearThemeSyncPending,
  getSystemTheme,
  hasPendingThemeSync,
  isThemeMode,
  markThemeSyncPending,
  readStoredMode,
  resolveTheme,
  writeStoredMode,
  type ResolvedTheme,
  type ThemeMode,
} from './theme';

export interface ThemeContextValue {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: (next: ThemeMode) => void;
  hydrateFromBackend: (pref: unknown) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Issues the `themePreference` write-through, clearing the pending-sync marker on confirmed
 * success and leaving it set (logged) on failure — never a silently swallowed rejection
 * (review-gate B3). Shared by `setMode`'s own write and the retry-on-next-load effect below. */
async function syncThemeToBackend(mode: ThemeMode): Promise<void> {
  try {
    const { error, response } = await apiClient.PATCH('/v1/profile', {
      body: { themePreference: mode },
    });
    if (error !== undefined || !response.ok) {
      console.error('Failed to sync theme preference to the backend.', error);
      return;
    }
    // code-review (2026-07-15, F4): pass `mode` (this call's own target) so an older, overlapping
    // write cannot clear a marker a newer write has since overwritten with its own target — see
    // `clearThemeSyncPending`'s doc comment in `theme.ts`.
    clearThemeSyncPending(mode);
  } catch (error) {
    console.error('Failed to sync theme preference to the backend.', error);
  }
}

/**
 * Theme state, `localStorage`-backed with a `PATCH /v1/profile` write-through (Task 12.2,
 * Resolution 17 — the backend hydrate/write-through path added directly in this activity, not
 * staged earlier). Write-through resilience (review-gate B3): a `THEME_PENDING_SYNC_KEY` marker
 * is set before every PATCH and cleared only on confirmed success, so `hydrateFromBackend` never
 * overwrites a change whose sync has not yet confirmed, and a failed sync is retried on the next
 * mount rather than silently lost.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(mode));
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Applies and persists whenever `mode` changes (including on mount, so the initial
  // `readStoredMode()` value is applied even though the no-FOUC bootstrap script in `index.html`
  // already applied it once, synchronously, before React mounted).
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
    writeStoredMode(mode);
  }, [mode]);

  // Live system-preference tracking, active only while `mode === 'system'`.
  useEffect(() => {
    if (mode !== 'system') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = getSystemTheme();
      setResolvedTheme(resolved);
      applyResolvedTheme(resolved);
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  // Retry-on-next-load (review-gate B3): a mode change made just before the tab closed/reloaded,
  // whose PATCH never confirmed, is retried here before any `hydrateFromBackend` adoption can be
  // honored — the pending marker itself already blocks that adoption while it stays set.
  useEffect(() => {
    if (hasPendingThemeSync()) {
      void syncThemeToBackend(modeRef.current);
    }
    // Runs once on mount only (`modeRef` is a ref, exempt from the dependency array), mirroring
    // the no-FOUC bootstrap's own "resolve once at start" scope.
  }, []);

  function setMode(next: ThemeMode): void {
    setModeState(next);
    markThemeSyncPending(next);
    void syncThemeToBackend(next);
  }

  // Wrapped in `useCallback` with a stable, empty dependency array (every value it reads —
  // `modeRef`, `setModeState`, the imported module functions — is itself stable across renders)
  // so `OperatorProvider`'s consuming mount-effect can safely omit it from its own dependency
  // array without a stale-closure risk.
  const hydrateFromBackend = useCallback((pref: unknown): void => {
    if (!isThemeMode(pref) || pref === modeRef.current || hasPendingThemeSync()) {
      return;
    }
    setModeState(pref);
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode, hydrateFromBackend }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
